import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../hooks/hooks";

interface RelatedProvidersProps {
  productType?: string;
  title?: string;
}

export const RelatedProviders: React.FC<RelatedProvidersProps> = ({
  productType,
  title = "You Might Also Like",
}) => {
  const { data: appData } = useAppSelector((state) => state.appData);
  const selectedCountry = useAppSelector(
    (state) => state.locale.selectedCountry,
  );
  const location = useLocation();
  const navigate = useNavigate();

  // Parse location path: e.g., /products/data/MTN -> product = "data", provider = "MTN"
  const segments = location.pathname.split("/").filter(Boolean);
  const currentProductType =
    productType || (segments.length >= 2 ? segments[segments.length - 2] : "");
  const formattedProvider =
    segments.length >= 1 ? segments[segments.length - 1] : "";

  // Get current active provider
  const currentSection = appData?.[currentProductType];
  const currentProviders: any[] = Array.isArray(currentSection?.providers)
    ? currentSection.providers
    : [];

  const activeProvider = currentProviders.find(
    (item: { slug: string }) => item.slug === formattedProvider,
  );

  const relatedProviders = useMemo(() => {
    if (!activeProvider || !appData) return [];

    // Parse active provider's tags
    const currentTags: string[] = Array.isArray(activeProvider.search_tags)
      ? activeProvider.search_tags.map((tag: string) =>
          String(tag).toLowerCase().trim(),
        )
      : [];

    const matchedList: any[] = [];
    const seenIds = new Set<string | number>([activeProvider.id]);

    // 1. Search for matching search_tags across ALL app categories
    if (currentTags.length > 0) {
      Object.keys(appData).forEach((sectionKey) => {
        const section = appData[sectionKey];
        const productPlans = appData?.[sectionKey]?.plans ?? [];
        if (!section || typeof section !== "object") return;

        const sectionProviders = Array.isArray(section.providers)
          ? section.providers
          : [];

        sectionProviders.forEach((prov: any) => {
          if (seenIds.has(prov.id)) return;

          const provTags: string[] = Array.isArray(prov.search_tags)
            ? prov.search_tags.map((t: string) =>
                String(t).toLowerCase().trim(),
              )
            : [];

          // Match if any search tag intersects
          const hasMatch = provTags.some((tag) => currentTags.includes(tag));
          if (hasMatch) {
            const providerPlans = productPlans.filter(
              (plan: {
                category: { provider_id: any; provider: { id: any } };
                countries: number[];
              }) => {
                const matchesProvider =
                  plan.category?.provider_id === prov.id ||
                  plan.category?.provider?.id === prov.id;

                const matchesCountry = selectedCountry
                  ? plan.countries?.includes(selectedCountry.id)
                  : true;

                return matchesProvider && matchesCountry;
              },
            );

            const discounts = providerPlans.map(
              (plan: { discount: string; wallet_price: string }) => {
                const numericDiscount = Number(plan.discount) || 0;
                const numericWalletPrice = Number(plan.wallet_price) || 0;
                const hasDiscount =
                  numericDiscount > 0 && numericDiscount > numericWalletPrice;

                const discountPercent = hasDiscount
                  ? ((numericDiscount - numericWalletPrice) / numericDiscount) *
                    100
                  : 0;
                return discountPercent;
              },
            );

            const maxDiscount =
              discounts.length > 0 ? Math.max(...discounts, 0) : 0;
            seenIds.add(prov.id);
            matchedList.push({
              ...prov,
              discount_percentage: Math.round(maxDiscount),
              sectionKey,
            });
          }
        });
      });
    }

    return matchedList;
  }, [
    appData,
    activeProvider,
    currentProviders,
    currentProductType,
    selectedCountry,
  ]);

  if (relatedProviders.length === 0) return null;

  return (
    <div className="w-full bg-white dark:bg-[#FFFFFF1A] border border-border-border1 dark:border-tabs rounded-xl p-4 mt-4">
      <p className="font-semibold text-sm lg:text-base dark:text-white mb-3">
        {title}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-3">
        {relatedProviders.map((item: any) => (
          <div
            key={item.id}
            onClick={() =>
              navigate(`/products/${item.sectionKey}/${item.slug}`)
            }
            className="flex flex-col gap-1.5 cursor-pointer group"
          >
            {/* Thumbnail */}
            <div className="relative aspect-[4/3] w-full rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800">
              <img
                src={item.image_url || "/png/placeholder.png"}
                alt={item.name}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              {item.discount_percentage && (
                <div className="absolute top-1.5 right-1.5 bg-white/95 dark:bg-black/90 backdrop-blur-sm text-red-500 font-semibold text-[10px] px-1.5 py-0.5 rounded-md shadow-xs">
                  {item.discount_percentage}% off
                </div>
              )}
            </div>

            {/* Title */}
            <p className="text-xs font-medium text-gray-800 dark:text-gray-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-1">
              {item.name}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};
